import React, { useState, useEffect } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import SearchSelect from "react-select";
import { api } from "../api";
import { toast } from "react-toastify";

interface RemoveScheduleByDriverModalProps {
  show: boolean;
  onHide: () => void;
  onRemoveSuccess: () => void;
  siteId: string;
  templateId: string;
  scheduleType: "pre-schedule" | "template" | "regular" | "daily";
  existingEvents: any[];
  carList?: any[];
}

interface DriverOption {
  value: string;
  label: string;
}

interface ScheduleEvent {
  pathId: string;
  carData: {
    _id: string;
    name: string;
  };
  normalDriver?: string;
  title: string;
  start: string;
  end: string;
}

const RemoveScheduleByDriverModal: React.FC<RemoveScheduleByDriverModalProps> = ({
  show,
  onHide,
  onRemoveSuccess,
  siteId,
  templateId,
  scheduleType,
  existingEvents,
  carList,
}) => {
  const [drivers, setDrivers] = useState<DriverOption[]>([]);
  const [selectedDriver, setSelectedDriver] = useState<DriverOption | null>(
    null
  );
  const [filteredSchedules, setFilteredSchedules] = useState<ScheduleEvent[]>(
    []
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const [removingPathIds, setRemovingPathIds] = useState<Set<string>>(
    new Set()
  );

  // Fetch drivers when modal opens
  useEffect(() => {
    if (show) {
      fetchDrivers();
      // Reset state
      setSelectedDriver(null);
      setFilteredSchedules([]);
      setRemovingPathIds(new Set());
    }
  }, [show, siteId]);

  // Filter schedules when driver is selected
  useEffect(() => {
    if (selectedDriver && existingEvents.length > 0) {
      const filtered = existingEvents.filter((event: any) => {
        const normalDriverId = event.extendedProps?.normalDriver;
        const hasReached = event.extendedProps?.hasReached;
        // Filter by driver and exclude schedules that have reached locations
        return normalDriverId === selectedDriver.value && !hasReached;
      });

      // Sort by start time
      const sorted = filtered.sort((a: any, b: any) => {
        const timeA = new Date(a.start || a.extendedProps?.start).getTime();
        const timeB = new Date(b.start || b.extendedProps?.start).getTime();
        return timeA - timeB;
      });

      setFilteredSchedules(sorted);
    } else {
      setFilteredSchedules([]);
    }
  }, [selectedDriver, existingEvents]);

  const fetchDrivers = async () => {
    setIsLoading(true);
    try {
      const { data }: any = await api({
        url: `/user/list/?role=DRIVER`,
        method: "get",
      });

      if (data.serverResponse.isError) {
        toast.error(data.serverResponse.message);
      } else {
        const driverData = data.result.data.userData.map(
          (entity: any, ind: number) => {
            return {
              value: entity._id,
              label: entity.name,
              carId: entity?.carId?._id,
            };
          }
        );
        setDrivers(driverData);
      }
    } catch (err) {
      toast.error(
        (err as any).response?.data?.message ||
          (err as any).message ||
          "Failed to fetch drivers"
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoveSchedules = async () => {
    if (!selectedDriver || filteredSchedules.length === 0) {
      toast.warning("Please select a driver with schedules");
      return;
    }

    setIsRemoving(true);
    let successCount = 0;
    let failCount = 0;

    try {
      // Delete schedules one by one
      for (const schedule of filteredSchedules) {
        try {
          // Get the pathId/scheduleId from the correct location
          const pathId =
            schedule.pathId || (schedule as any).extendedProps?.pathId;
          const scheduleId =
            (schedule as any).extendedProps?.scheduleId || pathId;

          // Determine the delete endpoint based on scheduleType
          const deleteEndpoint =
            scheduleType === "daily"
              ? `/schedule/delete/${scheduleId}`
              : `/path/delete/${pathId}`;

          const itemId = scheduleType === "daily" ? scheduleId : pathId;

          if (!itemId) {
            console.error("Missing ID for schedule:", schedule);
            failCount++;
            continue;
          }

          // Add to removing set for UI feedback
          setRemovingPathIds((prev) => new Set(prev).add(itemId));

          await api({
            url: deleteEndpoint,
            method: "delete",
          });

          // Remove from removing set after successful deletion
          setRemovingPathIds((prev) => {
            const newSet = new Set(prev);
            newSet.delete(itemId);
            return newSet;
          });

          // Remove from filtered list
          setFilteredSchedules((prev) =>
            prev.filter((s) => {
              const sPathId = s.pathId || (s as any).extendedProps?.pathId;
              const sScheduleId =
                (s as any).extendedProps?.scheduleId || sPathId;
              return (
                (scheduleType === "daily" ? sScheduleId : sPathId) !== itemId
              );
            })
          );

          successCount++;
        } catch (err) {
          console.error(`Failed to delete schedule:`, err);
          failCount++;

          // Remove from removing set on error
          const pathId =
            schedule.pathId || (schedule as any).extendedProps?.pathId;
          const scheduleId =
            (schedule as any).extendedProps?.scheduleId || pathId;
          const itemId = scheduleType === "daily" ? scheduleId : pathId;
          if (itemId) {
            setRemovingPathIds((prev) => {
              const newSet = new Set(prev);
              newSet.delete(itemId);
              return newSet;
            });
          }
        }
      }

      if (successCount > 0) {
        toast.success(
          `${successCount} schedule(s) removed successfully${
            failCount > 0 ? `, ${failCount} failed` : ""
          }`
        );
        if (successCount === filteredSchedules.length) {
          onRemoveSuccess();
          onHide();
        }
      } else {
        toast.error("Failed to remove any schedules");
      }
    } catch (err) {
      toast.error(
        (err as any).response?.data?.message ||
          (err as any).message ||
          "Failed to remove schedules"
      );
    } finally {
      setIsRemoving(false);
      setRemovingPathIds(new Set());
    }
  };

  const formatTime = (isoString: string) => {
    if (!isoString) return "";
    return new Date(isoString).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getCarName = (carData: any) => {
    // If carData has a name, use it
    if (carData?.name) {
      return carData.name;
    }
    // If carData only has _id and no name, lookup from carList
    if (carData?._id && carList?.length) {
      const car = carList.find((c: any) => c._id === carData._id);
      return car?.name || "N/A";
    }
    // If carData is just an ID string, lookup from carList
    if (typeof carData === "string" && carList?.length) {
      const car = carList.find((c: any) => c._id === carData);
      return car?.name || "N/A";
    }
    return "N/A";
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Remove Schedules by Driver</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <Form.Group className="mb-3">
            <Form.Label>Select Driver</Form.Label>
            <SearchSelect
              value={selectedDriver}
              onChange={(option: any) => setSelectedDriver(option)}
              options={drivers}
              isLoading={isLoading}
              placeholder="Select a driver..."
              isClearable
              isDisabled={isLoading}
            />
          </Form.Group>

          {selectedDriver && (
            <div className="mt-4">
              <h6>Schedules to Remove ({filteredSchedules.length})</h6>
              {filteredSchedules.length > 0 ? (
                <div
                  className="schedule-list"
                  style={{ maxHeight: "300px", overflowY: "auto" }}
                >
                  {filteredSchedules.map((schedule: any, index) => {
                    const pathId =
                      schedule.pathId || schedule.extendedProps?.pathId;
                    const isRemoving = removingPathIds.has(pathId);

                    return (
                      <div
                        key={pathId || index}
                        className={`border p-3 mb-2 rounded ${
                          isRemoving ? "bg-warning" : "bg-light"
                        }`}
                        style={{
                          opacity: isRemoving ? 0.6 : 1,
                          transition: "all 0.3s ease",
                        }}
                      >
                        <div className="d-flex justify-content-between align-items-center">
                          <div>
                            <strong>Car:</strong>{" "}
                            {getCarName(
                              schedule.carData ||
                                schedule.extendedProps?.carData
                            )}
                            <br />
                            <strong>Path:</strong>{" "}
                            {schedule.title || "Untitled"}
                            <br />
                            <strong>Time:</strong> {formatTime(schedule.start)}{" "}
                            - {formatTime(schedule.end)}
                          </div>
                          {isRemoving && (
                            <div
                              className="spinner-border spinner-border-sm text-danger"
                              role="status"
                            >
                              <span className="sr-only">Deleting...</span>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="alert alert-info">
                  No schedules found for {selectedDriver.label}.
                </div>
              )}
            </div>
          )}
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={isRemoving}>
          Cancel
        </Button>
        <Button
          variant="danger"
          onClick={handleRemoveSchedules}
          disabled={
            !selectedDriver || filteredSchedules.length === 0 || isRemoving
          }
        >
          {isRemoving
            ? `Removing... (${filteredSchedules.length} remaining)`
            : `Remove ${filteredSchedules.length} Schedule(s)`}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default RemoveScheduleByDriverModal;
